import React, { useEffect, useMemo, useState } from "react";
import { api, formatError } from "../../lib/api.js";
import { fmtDateTime } from "../../lib/format.js";
import { Plus, Edit3, Power, PowerOff, Check, X as XIcon, Trash2, AlertTriangle, Tag, MapPin } from "lucide-react";
import { Badge, Note, inputCls } from "./_shared.jsx";

const labelPt = (k) => k === "brand" ? "marca" : "filial";
const labelPtPlural = (k) => k === "brand" ? "marcas" : "filiais";

export function LookupTab({ kind }) {
  // kind === "brand" | "branch"
  const isBrand = kind === "brand";
  const Icon = isBrand ? Tag : MapPin;
  const label = isBrand ? "brand" : "branch";
  const labelPlural = isBrand ? "brands" : "branches";
  const endpoint = isBrand ? "/brands" : "/branches";

  const [items, setItems] = useState([]);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingName, setEditingName] = useState("");
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null); // {id, name}
  const [deleting, setDeleting] = useState(false);

  const load = async () => {
    const { data } = await api.get(`${endpoint}?include_inactive=true`);
    // Sort: active first (alphabetical), then inactive (alphabetical)
    setItems([...data].sort((a, b) => (a.active === b.active ? a.name.localeCompare(b.name) : a.active ? -1 : 1)));
  };

  useEffect(() => { load(); }, [kind]);

  const create = async () => {
    setError("");
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      await api.post(endpoint, { name });
      setNewName("");
      load();
    } catch (e) {
      setError(formatError(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  const startRename = (item) => {
    setEditingId(item.id);
    setEditingName(item.name);
    setError("");
  };

  const cancelRename = () => {
    setEditingId(null);
    setEditingName("");
  };

  const saveRename = async (item) => {
    setError("");
    const name = editingName.trim();
    if (!name || name === item.name) {
      cancelRename();
      return;
    }
    try {
      await api.patch(`${endpoint}/${item.id}`, { name });
      cancelRename();
      load();
    } catch (e) {
      setError(formatError(e.response?.data?.detail));
    }
  };

  const toggleActive = async (item) => {
    setError("");
    try {
      await api.patch(`${endpoint}/${item.id}`, { active: !item.active });
      load();
    } catch (e) {
      setError(formatError(e.response?.data?.detail));
    }
  };

  // Permanently delete a brand/branch. Backend returns 409 with details when the
  // record is still referenced elsewhere — surface that as an inline error.
  const requestDelete = (item) => {
    setError("");
    setConfirmDelete({ id: item.id, name: item.name });
  };

  const doDelete = async () => {
    if (!confirmDelete) return;
    setError("");
    setDeleting(true);
    try {
      await api.delete(`${endpoint}/${confirmDelete.id}`);
      setConfirmDelete(null);
      load();
    } catch (e) {
      const detail = e.response?.data?.detail;
      const msg = typeof detail === "object" && detail?.message ? detail.message : formatError(detail);
      setError(msg);
      setConfirmDelete(null);
    } finally {
      setDeleting(false);
    }
  };

  const activeCount = items.filter((i) => i.active).length;
  const inactiveCount = items.length - activeCount;

  return (
    <div>
      <div className="flex items-end justify-between mb-3">
        <div>
          <h2 className="font-heading text-base font-bold text-[#1A1A2E] flex items-center gap-2">
            <Icon className="w-4 h-4 text-[#1F3864]" />
            {isBrand ? "Brands" : "Branches"}
          </h2>
          <div className="text-xs text-[#6C757D] mt-0.5">
            {activeCount} active{inactiveCount > 0 && <> · <span className="text-[#6C757D]">{inactiveCount} deactivated</span></>}
            <span className="ml-2">Used dynamically across every dropdown, filter and assignment.</span>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-md p-3 mb-4 flex items-center gap-2">
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && create()}
          placeholder={`Nome ${labelPt(label)}`}
          className={inputCls + " flex-1 max-w-xs"}
          data-testid={`new-${label}-input`}
        />
        <button
          onClick={create}
          disabled={creating || !newName.trim()}
          className="bg-[#1F3864] hover:bg-[#142542] text-white px-3 py-1.5 rounded-sm text-sm flex items-center gap-1 disabled:opacity-60"
          data-testid={`new-${label}-btn`}
        >
          <Plus className="w-3 h-3" /> Adicionar {labelPt(label)}
        </button>
      </div>

      {error && <div className="px-3 py-2 mb-3 bg-[#F8D7DA] text-[#721C24] border border-[#DC3545]/30 rounded-sm text-sm" data-testid={`${label}-error`}>{error}</div>}

      <div className="bg-white border border-gray-200 rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F4F6F8] text-[10px] uppercase tracking-wider text-[#6C757D]">
            <tr>
              <th className="px-3 py-2 text-left">Nome</th>
              <th className="px-3 py-2 text-left">Estado</th>
              <th className="px-3 py-2 text-left">Criado</th>
              <th className="px-3 py-2 text-right w-80">Acções</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan="4" className="px-3 py-4 text-center text-xs text-[#6C757D]">Ainda sem {labelPtPlural(label)}.</td></tr>
            )}
            {items.map((it) => {
              const editing = editingId === it.id;
              const inactive = !it.active;
              return (
                <tr key={it.id} className={`border-t border-gray-100 ${inactive ? "bg-[#F4F6F8]" : ""}`} data-testid={`${label}-row-${it.id}`}>
                  <td className="px-3 py-2">
                    {editing ? (
                      <input
                        autoFocus
                        value={editingName}
                        onChange={(e) => setEditingName(e.target.value)}
                        onKeyDown={(e) => { if (e.key === "Enter") saveRename(it); if (e.key === "Escape") cancelRename(); }}
                        className={inputCls + " max-w-xs"}
                        data-testid={`${label}-edit-input-${it.id}`}
                      />
                    ) : (
                      <span className={`font-medium ${inactive ? "text-[#6C757D] line-through" : "text-[#1A1A2E]"}`}>{it.name}</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs">
                    {it.active ? <Badge tone="green">Activo</Badge> : <Badge tone="grey">Desactivado</Badge>}
                  </td>
                  <td className="px-3 py-2 text-xs text-[#6C757D]">{fmtDateTime(it.created_at)}</td>
                  <td className="px-3 py-2 text-xs text-right space-x-2">
                    {editing ? (
                      <>
                        <button onClick={() => saveRename(it)} className="inline-flex items-center gap-1 text-[#155724] hover:underline" data-testid={`${label}-save-${it.id}`}>
                          <Check className="w-3 h-3" /> Guardar
                        </button>
                        <button onClick={cancelRename} className="inline-flex items-center gap-1 text-[#6C757D] hover:underline">
                          <XIcon className="w-3 h-3" /> Cancelar
                        </button>
                      </>
                    ) : (
                      <>
                        <button onClick={() => startRename(it)} className="inline-flex items-center gap-1 text-[#1F3864] hover:underline" data-testid={`${label}-edit-${it.id}`}>
                          <Edit3 className="w-3 h-3" /> Renomear
                        </button>
                        {it.active ? (
                          <button onClick={() => toggleActive(it)} className="inline-flex items-center gap-1 text-[#856404] hover:underline" data-testid={`${label}-deactivate-${it.id}`}>
                            <PowerOff className="w-3 h-3" /> Desactivar
                          </button>
                        ) : (
                          <button onClick={() => toggleActive(it)} className="inline-flex items-center gap-1 text-[#155724] hover:underline" data-testid={`${label}-reactivate-${it.id}`}>
                            <Power className="w-3 h-3" /> Reactivar
                          </button>
                        )}
                        <button
                          onClick={() => requestDelete(it)}
                          className="inline-flex items-center gap-1 text-[#DC3545] hover:underline"
                          title="Apagar permanentemente — só disponível se não houver registos associados."
                          data-testid={`${label}-delete-${it.id}`}
                        >
                          <Trash2 className="w-3 h-3" /> Apagar
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <Note tone="info">
        <b>Desactivar</b> esconde {isBrand ? "a marca" : "o dealer"} dos formulários e dropdowns mas mantém todo o histórico intacto (reversível).{" "}
        <b>Apagar</b> remove permanentemente o registo — só está disponível quando nenhum outro registo o referencia (irreversível).
      </Note>

      {confirmDelete && (
        <ConfirmDeleteDialog
          kind={kind}
          name={confirmDelete.name}
          busy={deleting}
          onConfirm={doDelete}
          onCancel={() => setConfirmDelete(null)}
        />
      )}
    </div>
  );
}


export function ConfirmDeleteDialog({ kind, name, busy, onConfirm, onCancel }) {
  const isBrand = kind === "brand";
  const label = isBrand ? "marca" : "dealer";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={busy ? undefined : onCancel}>
      <div className="bg-white rounded-md shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid={`${label}-confirm-delete-dialog`}>
        <div className="px-5 py-3 border-b border-gray-200 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 text-[#DC3545]" />
          <h2 className="font-heading text-base font-bold text-[#1A1A2E]">Apagar permanentemente</h2>
        </div>
        <div className="p-5 space-y-3 text-sm text-[#1A1A2E]">
          <p>
            Tens a certeza que queres apagar {isBrand ? "a marca" : "o dealer"} <b className="font-mono">{name}</b>? Esta acção é permanente e não pode ser revertida.
          </p>
          <div className="px-3 py-2 bg-[#FFF3CD] border border-[#FFC107]/40 rounded-sm text-xs text-[#856404]">
            Verifica primeiro que esta {label} não está atribuída a nenhum utilizador ou registo. O sistema vai bloquear a operação caso ainda existam referências.
          </div>
        </div>
        <div className="px-5 py-3 border-t border-gray-200 flex justify-end gap-2">
          <button
            onClick={onCancel}
            disabled={busy}
            className="px-4 py-2 text-sm border border-gray-300 rounded-sm hover:bg-[#F4F6F8] disabled:opacity-60"
            data-testid={`${label}-confirm-delete-cancel`}
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            disabled={busy}
            className="px-4 py-2 text-sm bg-[#DC3545] hover:bg-[#a82532] text-white rounded-sm flex items-center gap-1 disabled:opacity-60"
            data-testid={`${label}-confirm-delete-submit`}
          >
            <Trash2 className="w-3 h-3" /> {busy ? "A apagar…" : "Apagar permanentemente"}
          </button>
        </div>
      </div>
    </div>
  );
}




